import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDots, faPencil } from "@fortawesome/pro-regular-svg-icons";
import CoContributorNoteDto from "../../../types/feedback/CoContributorNoteDto";
import feedbackRequestApi from "../../../api/feedback/feedbackRequestApi";
import { FeedbackRequestDto } from "../../../types/feedback/FeedbackRequestDto";
import { FormattedDate, TextArea } from "../../common";
import Avatar from "../../common/Avatar";
import SmallLoader from "../../loaders/SmallLoader";
import { canEditCoContributorNote } from "../../../helpers/coContributorNoteHelper";

interface CoContributorNotesSectionProps {
  notes: CoContributorNoteDto[];
  isLoading: boolean;
  /** When false, only the composer is shown (notes are displayed elsewhere, e.g. top feedback bar). */
  showNoteList?: boolean;
  /** The active feedback request for the logged-in user, if they are a recipient with FullReviewAccess */
  activeRequestForCurrentUser: FeedbackRequestDto | null;
  loggedInUserId: number;
  api: feedbackRequestApi;
  onNoteSaved: () => void;
}

function CoContributorNotesSection({
  notes,
  isLoading,
  showNoteList = true,
  activeRequestForCurrentUser,
  loggedInUserId,
  api,
  onNoteSaved,
}: CoContributorNotesSectionProps) {
  const { t } = useTranslation();
  const [noteBody, setNoteBody] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const canSubmitNote =
    activeRequestForCurrentUser !== null &&
    noteBody.trim().length > 0 &&
    !isSaving;

  const handleSaveNote = () => {
    if (!canSubmitNote || !activeRequestForCurrentUser) return;

    setIsSaving(true);
    setSaveError(null);

    api.saveNote(
      activeRequestForCurrentUser.id,
      { body: noteBody.trim() },
      () => {
        setIsSaving(false);
        setNoteBody("");
        onNoteSaved();
      },
      (err: any) => {
        setIsSaving(false);
        setSaveError(err?.message || "Failed to save note. Please try again.");
      },
    );
  };

  const startEditingNote = (note: CoContributorNoteDto) => {
    setEditingNoteId(note.id);
    setEditBody(note.body);
    setEditError(null);
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditBody("");
    setEditError(null);
  };

  const handleSaveEdit = (noteId: number) => {
    if (!editBody.trim() || isSavingEdit) return;

    setIsSavingEdit(true);
    setEditError(null);

    api.updateNote(
      noteId,
      { body: editBody.trim() },
      () => {
        setIsSavingEdit(false);
        cancelEditingNote();
        onNoteSaved();
      },
      (err: any) => {
        setIsSavingEdit(false);
        setEditError(
          err?.message || "Failed to save changes. Please try again.",
        );
      },
    );
  };

  if (isLoading) {
    return <SmallLoader />;
  }

  const hasNotes = notes.length > 0;
  const showInputBox = activeRequestForCurrentUser !== null;
  const requestMessage = activeRequestForCurrentUser?.message?.trim() ?? "";

  if (!hasNotes && !showInputBox) {
    return null;
  }

  const composerBlock = showInputBox && (
    <div
      className={
        showNoteList && hasNotes
          ? "px-4 py-3 border-t border-gray-100 bg-gray-50/50 rounded-b-lg"
          : "px-4 py-3"
      }
    >
      <p className="text-sm text-gray-600 leading-relaxed mb-3">
        {t("Feedback.CollabDoc.YourFeedbackIntro", {
          defaultValue:
            "Use this space to provide clear, constructive feedback that will be useful to those involved in this conversation",
        })}
      </p>
      {requestMessage && (
        <blockquote
          className="mb-3 rounded-r-md border-l-2 border-l-gray-300 bg-gray-50/90 px-3 py-2.5 text-sm text-gray-600 whitespace-pre-wrap break-words"
          aria-label={t("Feedback.CollabDoc.RequestMessageAria", {
            defaultValue: "Message from the person who requested feedback",
          })}
        >
          {requestMessage}
        </blockquote>
      )}
      <TextArea
        value={noteBody}
        onChange={setNoteBody}
        placeholder={t("Feedback.CollabDoc.YourFeedbackPlaceholder", {
          defaultValue: "Write your feedback notes here...",
        })}
        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        minRows={3}
        maxRows={8}
        maxLength={4000}
        isReadOnly={isSaving}
      />
      {saveError && <p className="text-xs text-red-600 mt-1">{saveError}</p>}
      <div className="flex justify-end gap-2 mt-2">
        <button
          className="btn-primary text-sm !px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSaveNote}
          disabled={!canSubmitNote}
        >
          {isSaving ? "Submitting..." : "Submit Notes"}
        </button>
      </div>
    </div>
  );

  if (!showNoteList) {
    return (
      <div className="mt-6 mb-4 w-full px-2 lg:px-4 print:max-w-full">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faCommentDots} className="text-gray-400" />
              {t("Feedback.CollabDoc.YourFeedbackTitle", {
                defaultValue: "Your Feedback",
              })}
            </h3>
          </div>
          {composerBlock}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 mb-4 w-full px-2 lg:px-4 print:max-w-full">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm !my-0 font-semibold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faCommentDots} className="text-gray-400" />
            {t("Feedback.CollabDoc.YourFeedbackTitle", {
              defaultValue: "Your Feedback",
            })}
          </h3>
        </div>

        {hasNotes && (
          <div className="divide-y divide-gray-100">
            {notes.map((note) => {
              const isEditing = editingNoteId === note.id;
              const showEditControl = canEditCoContributorNote(
                note,
                loggedInUserId,
              );

              return (
                <div key={note.id} className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 pt-0.5">
                      <Avatar
                        imageUrl={null}
                        userFullName={note.authorName ?? "Unknown"}
                        userInitials={
                          note.authorName
                            ? note.authorName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .substring(0, 2)
                            : "?"
                        }
                        size={8}
                        initialsTextClassName="text-xs"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800">
                          {note.authorName ?? "Unknown"}
                        </span>
                        <span className="text-xs text-gray-400">
                          <FormattedDate
                            date={new Date(note.dateCreated)}
                            displayMode="FROM-NOW"
                            convertFromUtc
                          />
                        </span>
                        {note.dateUpdated && (
                          <span className="text-xs text-gray-400 italic">
                            edited{" "}
                            <FormattedDate
                              date={new Date(note.dateUpdated)}
                              displayMode="FROM-NOW"
                              convertFromUtc
                            />
                          </span>
                        )}
                        {showEditControl && !isEditing && (
                          <button
                            type="button"
                            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 ml-auto"
                            onClick={() => startEditingNote(note)}
                            title="Edit feedback"
                          >
                            <FontAwesomeIcon
                              icon={faPencil}
                              className="w-3 h-3"
                            />
                            Edit
                          </button>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="mt-2">
                          <textarea
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white resize-none focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                            rows={3}
                            maxLength={4000}
                          />
                          {editError && (
                            <p className="text-xs text-red-600 mt-1">
                              {editError}
                            </p>
                          )}
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              type="button"
                              className="btn-secondary text-sm px-3 py-1.5"
                              onClick={cancelEditingNote}
                              disabled={isSavingEdit}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleSaveEdit(note.id)}
                              disabled={!editBody.trim() || isSavingEdit}
                            >
                              {isSavingEdit ? "Saving..." : "Save"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                          {note.body}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {composerBlock}
      </div>
    </div>
  );
}

export default CoContributorNotesSection;
